/**
 * Wires the domain's injected dependencies to their real implementations, once,
 * at start-up.
 *
 * It also registers the RemoteStore adapter: `src/cloud/sync` talks only to the
 * port, so exactly one place decides which backend is behind it — Google Drive
 * or Koofr, per `settings.cloud.provider`. Drive is registered synchronously so
 * the first render never sees an unconfigured store; a Koofr user is switched
 * over as soon as the settings row has been read.
 *
 * The inbox domain deliberately does not import the AI, Drive or media modules:
 * it declares what it needs (`InboxDeps`) so its tests can run without a network
 * or a canvas, and so `src/domain` keeps pointing only at `src/db`. That means
 * something has to connect the two at the edge of the app — this file — and the
 * app entry point must import it before the first render. Without it the receipt
 * pipeline silently reports "Kvittona väntar på AI" even when a provider is
 * configured, and booked receipts are never filed in Drive.
 *
 * Import for the side effect only: `import './wiring'`.
 *
 * The AI module is loaded on demand: it carries the Vercel AI SDK and zod
 * schemas (~130 kB) that a user who never connects a provider should not pay
 * for on first paint.
 */
import { driveStore } from '@/cloud/drive/adapter';
import { setRemoteStore } from '@/cloud/store';
import { getSettings } from '@/domain/settings/service';
import { installSharedLedger, onLedgerEvent, startLedgerWatch } from '@/cloud/sync/ledger';
import { fileReceipt, fileRejected, uploadReceiptCopy } from '@/cloud/sync/receipts';
import { backupScheduler } from '@/cloud/sync/scheduler';
import { ensureDeviceIdentity, registerDevice } from '@/domain/devices/service';
import { configureInboxDeps } from '@/domain/inbox';
import { makeThumbnail, shrinkImage } from '@/media/image';
import { receiptHydration } from '@/media/receipt-hydration';

/** Filled the first time the AI module is loaded, so `toUiError` can be sync. */
let aiErrors: { toUiError: (err: unknown) => ReturnType<typeof import('@/ai').toUiError> } | null = null;
void import('@/ai/errors').then((m) => {
  aiErrors = m;
});

/**
 * Swap in Koofr when the user has connected it. Loaded on demand so a Drive
 * user never pays for the Koofr client, and failure is survivable: the store
 * stays on Drive and Mer › Lagring › Koofr can reconnect.
 */
async function selectCloudAdapter(): Promise<void> {
  try {
    const { cloud } = await getSettings();
    if (cloud.provider !== 'koofr') return;
    const { installKoofrCredentials, koofrStore } = await import('@/cloud/koofr');
    installKoofrCredentials();
    setRemoteStore(koofrStore);
  } catch {
    // Dexie unavailable or settings unreadable; Drive stays registered.
  }
}

let wired = false;

/** Idempotent: React StrictMode and hot reloads must not double-register. */
export function wireApp(): void {
  if (wired) return;
  wired = true;

  setRemoteStore(driveStore);
  const adapterReady = selectCloudAdapter();

  configureInboxDeps({
    /*
     * Thin adapters, not casts: the domain declares its optional fields as
     * `T | undefined` (exactOptionalPropertyTypes) while the AI and Drive
     * modules declare plain optionals, so each side is rebuilt with only the
     * keys it actually has.
     */
    readReceipt: async (input, settings, deps) => {
      const { readReceipt } = await import('@/ai');
      return readReceipt(input, settings, deps.signal ? { signal: deps.signal } : {});
    },
    classifyBankRows: async (rows, ctx, settings, deps) => {
      const { classifyBankRows } = await import('@/ai');
      return classifyBankRows(rows, ctx, settings, deps.signal ? { signal: deps.signal } : {});
    },
    // Synchronous by contract, so it cannot be imported on demand. Kept as a
    // best-effort passthrough until the AI module has been loaded once; the
    // pipeline's own Swedish fallbacks cover the window before that.
    toUiError: (err) => aiErrors?.toUiError(err) ?? { kind: 'other', message: 'Något gick fel med AI-tjänsten.', technical: String(err) },
    // Filing in the remote store is best effort: a booking must never fail
    // because the archive copy could not be moved. The archive check re-uploads
    // later.
    fileReceipt: (input, db) => fileReceipt(input, db),
    uploadReceipt: (input, db) => uploadReceiptCopy(input, db),
    fileRejected: (input, db) => fileRejected({ companyId: input.companyId, driveFileId: input.driveFileId, reason: input.reason, ...(input.date === undefined ? {} : { date: input.date }) }, db),
    markDirty: (companyId) => backupScheduler.markDirty(companyId),
    media: { shrinkImage, makeThumbnail },
  });

  /*
   * Establish this installation's identity before anything can book: the
   * booking path reads it synchronously (`currentDeviceId`) and silently
   * leaves `deviceId` off when it is not there yet. Registering it for the
   * active company gives the id a name in the backup.
   */
  void (async () => {
    try {
      await ensureDeviceIdentity();
      const { activeCompanyId } = await getSettings();
      if (activeCompanyId) await registerDevice(activeCompanyId);
    } catch {
      // Dexie unavailable; verifikationer booked in this session carry no device.
    }
  })();

  /*
   * The shared ledger: bookings on a company whose archive lives in a store
   * with conditional writes (Koofr) are serialised through the remote copy,
   * and another device's changes are merged in — before every booking, and
   * by the watcher while the app is open. A merge that brought receipt
   * index rows brings the bytes home the same way a restore does.
   */
  installSharedLedger();
  onLedgerEvent((event) => {
    if (event.type === 'merged' && event.summary.attachments > 0) void receiptHydration.run().catch(() => undefined);
  });

  /*
   * The watcher's first pull and the scheduler's on-open backup both ask the
   * active store, so they wait for the adapter choice above: a Koofr user
   * whose first tick ran against the default Drive store would be told to
   * sign in to Google. `start()` stays idempotent; Hem calls it too.
   */
  void adapterReady.then(() => {
    startLedgerWatch({ companyId: async () => (await getSettings()).activeCompanyId });
    backupScheduler.start();
  });

  // Resume fetching the receipts a restore has not brought home yet. Costs two
  // indexed reads when there are none, and pauses by itself without a token.
  void receiptHydration.run().catch(() => undefined);
}

wireApp();
